'use client'

// src/tooltip.web.tsx
import * as Tooltip from "@radix-ui/react-tooltip";
import { useAugmentedRef, useIsomorphicLayoutEffect } from "@rn-primitives/hooks";
import * as Slot from "@rn-primitives/slot";
import * as React from "react";
import { Pressable as Pressable2, View as View2 } from "react-native";
var RootContext = React.createContext(null);
var Root2 = React.forwardRef(
  ({
    asChild,
    delayDuration,
    skipDelayDuration,
    disableHoverableContent,
    onOpenChange: onOpenChangeProp,
    ...viewProps
  }, ref) => {
    const [open, setOpen] = React.useState(false);
    function onOpenChange(value) {
      setOpen(value);
      onOpenChangeProp?.(value);
    }
    const Component = asChild ? Slot.View : View2;
    return <RootContext.Provider value={{ open, onOpenChange }}><Tooltip.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      disableHoverableContent={disableHoverableContent}
    ><Tooltip.Root
      open={open}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration}
      disableHoverableContent={disableHoverableContent}
    ><Component ref={ref} {...viewProps} /></Tooltip.Root></Tooltip.Provider></RootContext.Provider>;
  }
);
Root2.displayName = "RootWebTooltip";
function useTooltipContext() {
  const context = React.useContext(RootContext);
  if (!context) {
    throw new Error("Tooltip compound components cannot be rendered outside the Tooltip component");
  }
  return context;
}
var Trigger2 = React.forwardRef(
  ({ asChild, onPress: onPressProp, role: _role, disabled, ...props }, ref) => {
    const { onOpenChange, open } = useTooltipContext();
    const augmentedRef = useAugmentedRef({
      ref,
      methods: {
        open() {
          onOpenChange(true);
        },
        close() {
          onOpenChange(false);
        }
      }
    });
    function onPress(ev) {
      if (onPressProp) {
        onPressProp(ev);
      }
      onOpenChange(!open);
    }
    useIsomorphicLayoutEffect(() => {
      if (augmentedRef.current) {
        const augRef = augmentedRef.current;
        augRef.dataset.state = open ? "open" : "closed";
        augRef.type = "button";
      }
    }, [open]);
    const Component = asChild ? Slot.Pressable : Pressable2;
    return <Tooltip.Trigger disabled={disabled ?? void 0} asChild><Component
      ref={augmentedRef}
      onPress={onPress}
      role='button'
      disabled={disabled}
      {...props}
    /></Tooltip.Trigger>;
  }
);
Trigger2.displayName = "TriggerWebTooltip";
function Portal2({ forceMount, container, children }) {
  return <Tooltip.Portal forceMount={forceMount} children={children} container={container} />;
}
var Overlay = React.forwardRef(
  ({ asChild, forceMount, ...props }, ref) => {
    const Component = asChild ? Slot.Pressable : Pressable2;
    return <Component ref={ref} {...props} />;
  }
);
Overlay.displayName = "OverlayWebTooltip";
var Content2 = React.forwardRef(
  ({
    asChild = false,
    forceMount,
    align = "center",
    side = "top",
    sideOffset = 0,
    alignOffset = 0,
    avoidCollisions = true,
    insets: _insets,
    disablePositioningStyle: _disablePositioningStyle,
    onCloseAutoFocus: _onCloseAutoFocus,
    onEscapeKeyDown,
    onInteractOutside: _onInteractOutside,
    onPointerDownOutside,
    sticky,
    hideWhenDetached,
    ...props
  }, ref) => {
    const Component = asChild ? Slot.View : View2;
    return <Tooltip.Content
      onEscapeKeyDown={onEscapeKeyDown}
      onPointerDownOutside={onPointerDownOutside}
      forceMount={forceMount}
      align={align}
      side={side}
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      avoidCollisions={avoidCollisions}
      sticky={sticky}
      hideWhenDetached={hideWhenDetached}
    ><Component ref={ref} {...props} /></Tooltip.Content>;
  }
);
Content2.displayName = "ContentWebTooltip";
export {
  Content2 as Content,
  Overlay,
  Portal2 as Portal,
  Root2 as Root,
  Trigger2 as Trigger
};
